import { useReactiveVar } from '@apollo/client/react';
import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import { Box, Button, ListItemText, Paper, Stack, Typography } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import useUnregisterMobileDevice from 'hooks/useUnregisterMobileDevice/useUnregisterMobileDevice';
import * as React from 'react';
import { authVar } from 'state/vars';
import QRCodeValueUtil from 'utils/QRCodeValue/QRCodeValue.util';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import Loader from 'v2/components/Loader/Loader';
import ModalTemplate from 'v2/components/Modal/ModalTemplate';
import useModalContext from 'v2/components/Modal/useModalContext';
import QRCode from 'v2/components/QRCode/QRCode';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';

const MyDevice: React.FC = () => {
  const { translation } = useTranslation();
  const { user } = useReactiveVar(authVar);
  const { openDrawer, closeDrawer } = useDrawerContext();
  const { openModal, closeModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();

  const accessDevice = user?.mobileDevices?.find((device) => device.role === 'ACCESS');
  const isRegistered = accessDevice?.isRegistered ?? false;

  const prevIsRegisteredRef = React.useRef(isRegistered);
  React.useEffect(() => {
    if (!prevIsRegisteredRef.current && isRegistered) {
      closeDrawer();
      showSnackbar(translation.app_connected, { severity: 'success' });
    }
    prevIsRegisteredRef.current = isRegistered;
  }, [isRegistered, closeDrawer, showSnackbar, translation.app_connected]);

  const qrCodeValue = accessDevice?.registrationToken
    ? QRCodeValueUtil.getQrCodeValue(accessDevice.registrationToken)
    : '';

  const { unregisterMobileDeviceById } = useUnregisterMobileDevice({
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const handleDisconnect = async () => {
    if (!user?.id || !accessDevice?.id) return;
    const result = await unregisterMobileDeviceById(user.id, accessDevice.id);
    if (result?.unregisterMobileDeviceById) {
      showSnackbar(translation.app_disconnected, { severity: 'success' });
      closeModal();
      closeDrawer();
    }
  };

  const openDisconnectModal = () => {
    openModal(
      <ModalTemplate
        title={translation.disconnect_device}
        primaryAction={{
          label: translation.confirm,
          onClick: handleDisconnect,
          dangerzone: true,
          id: 'confirm-disconnect-device-button'
        }}
        secondaryAction={{ label: translation.cancel, id: 'cancel-disconnect-device-button' }}
      >
        <Typography>{translation.access_profile_unlink_notice}</Typography>
      </ModalTemplate>
    );
  };

  const handleConnectClick = () => {
    openDrawer(
      <DrawerTemplate
        title={translation.atlas_access_app}
        secondaryAction={{ label: translation.cancel, id: 'connect-device-cancel-button' }}
      >
        <Stack gap={3}>
          {qrCodeValue ? (
            <QRCode value={qrCodeValue} sx={{ alignSelf: 'center' }} id="drawer-qr-code" />
          ) : (
            <Stack alignItems="center" gap={1}>
              <Loader />
              <Typography variant="body2">{translation.generating_qr}</Typography>
            </Stack>
          )}
          <Typography variant="body2">{translation.scan_qr_to_link_device}</Typography>
        </Stack>
      </DrawerTemplate>
    );
  };

  return (
    <>
      {user?.isAdmin && (
        <BreadcrumbMeta
          breadcrumb={{
            title: translation.my_device,
            crumbs: [{ text: translation.my_profile, path: '/my-profile/my-device' }]
          }}
        />
      )}
      <Stack gap={2}>
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            {isRegistered ? <CheckCircleOutline color="success" /> : <CancelOutlined color="disabled" />}
            <ListItemText
              primary={translation.atlas_access_application}
              secondary={isRegistered ? translation.access_profile_linked : translation.access_profile_not_linked}
            />
          </Stack>
        </Paper>
        <Box>
          {isRegistered ? (
            <Button id="disconnect-device-button" color="error" variant="outlined" onClick={openDisconnectModal}>
              {translation.disconnect_device}
            </Button>
          ) : (
            <Button id="connect-device-button" variant="outlined" onClick={handleConnectClick}>
              {translation.connect_device}
            </Button>
          )}
        </Box>
      </Stack>
    </>
  );
};

export default MyDevice;
